import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronDown, Globe, Users, HandHeart, Eye, Trophy, Camera, Instagram, MessageCircle, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const ELLA_PHOTOS = [
  { url: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/tl5ritq5_0b428b53-6add-4c0b-b93b-92d360f06905.JPG", alt: "Ella on her tricycle" },
  { url: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/e1joqc8a_2ce88934-44eb-490c-a528-f9343bfa57e6.JPG", alt: "Ella at the table" },
  { url: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/57q9tq5d_a1afcd51-6c09-4419-b7f1-8deacb19c643.JPG", alt: "Ella laughing on a rocking horse" },
  { url: "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/uv7iwdrd_bf44386e-0f20-49ae-9e26-967a7bda7b45.JPG", alt: "Ella smiling" },
];

const stats = [
  { icon: HandHeart, value: "140,000+", label: "Euros Donated", color: "#b46eb6" },
  { icon: Users, value: "1,814", label: "Runners", color: "#7DD4B5" },
  { icon: Eye, value: "2,000+", label: "Visitors", color: "#b46eb6" },
  { icon: Trophy, value: "130", label: "Volunteers", color: "#7DD4B5" },
];

const symptoms = [
  { title: "Loss of Hand Use", desc: "Ella can no longer use her hands purposefully. Instead, repetitive hand movements occur." },
  { title: "Loss of Speech", desc: "Most children with Rett syndrome lose the ability to speak. Ella cannot tell us how she feels." },
  { title: "Motor Impairment", desc: "Walking becomes difficult or impossible. Ella cannot walk on her own." },
  { title: "Breathing Issues", desc: "Irregular breathing patterns including hyperventilation and breath-holding episodes." },
  { title: "Seizures", desc: "Many children with Rett syndrome develop epilepsy over the course of their lives." },
  { title: "Scoliosis", desc: "Curvature of the spine is common and may require surgical treatment." },
];

export default function EnglishPage() {
  const [lightbox, setLightbox] = useState(null);

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div data-testid="english-page" className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-nav shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/">
              <img src="/logo-runforrett.svg" alt="Run for Rett Logo" className="h-12 md:h-14 w-auto" />
            </Link>
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              <button onClick={() => scrollTo("#ella-en")} className="text-sm font-medium text-gray-600 hover:text-[#b46eb6] transition-colors">Ella's Story</button>
              <button onClick={() => scrollTo("#rett-en")} className="text-sm font-medium text-gray-600 hover:text-[#b46eb6] transition-colors">Rett Syndrome</button>
              <button onClick={() => scrollTo("#event-en")} className="text-sm font-medium text-gray-600 hover:text-[#b46eb6] transition-colors">The Event</button>
              <Button onClick={() => scrollTo("#donate-en")} className="rounded-full px-6 font-semibold bg-[#b46eb6] hover:bg-[#8f5691] text-white">
                Donate
              </Button>
              <Link to="/">
                <Button variant="ghost" className="rounded-full px-4 text-sm font-medium text-gray-500 hover:text-[#b46eb6] hover:bg-[#b46eb6]/5">
                  <Globe className="w-4 h-4 mr-1.5" /> Deutsch
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden" style={{ background: "linear-gradient(135deg, #faf5fb 0%, #ffffff 40%, #e8faf3 100%)" }}>
        <div className="absolute top-20 right-0 w-96 h-96 rounded-full opacity-10" style={{ background: "#7DD4B5", filter: "blur(100px)" }} />
        <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full opacity-10" style={{ background: "#b46eb6", filter: "blur(80px)" }} />
        <img src="/runforrett-silhouette.png" alt="" aria-hidden="true" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] md:w-[700px] lg:w-[800px] opacity-[0.04] pointer-events-none select-none" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 text-center">
          <div className="space-y-6 md:space-y-8 animate-fade-in-up">
            <p className="font-accent text-2xl md:text-3xl" style={{ color: "#7DD4B5" }}>Running together. Giving hope together.</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-tight" style={{ color: "#1F2937" }}>
              <span className="block" style={{ color: "#b46eb6" }}>Running for a Future</span>
              <span className="block">without Rett Syndrome</span>
            </h1>
            <p className="text-base md:text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: "#4B5563" }}>
              The next <strong>Run for Rett</strong> is coming soon. Stay tuned.<br />
              Join us, run with us, or support research for a future without Rett syndrome.
            </p>
            <div className="flex flex-wrap justify-center gap-4 pt-2">
              <Button onClick={() => scrollTo("#donate-en")} className="rounded-full px-8 py-6 text-base font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 bg-[#b46eb6] hover:bg-[#8f5691] text-white">
                Donate
              </Button>
              <Button onClick={() => scrollTo("#ella-en")} variant="outline" className="rounded-full px-8 py-6 text-base font-semibold border-2 border-[#b46eb6]/20 hover:border-[#b46eb6] hover:bg-[#b46eb6]/5 text-[#b46eb6] transition-all">
                Ella's Story
              </Button>
            </div>
          </div>
        </div>
        <button onClick={() => scrollTo("#ella-en")} className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8" style={{ color: "#7DD4B5" }} />
        </button>
      </section>

      {/* Ella's Story */}
      <section id="ella-en" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <p className="font-accent text-xl md:text-2xl mb-2" style={{ color: "#7DD4B5" }}>A very special story</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight" style={{ color: "#1F2937" }}>Hi, I'm Ella.</h2>
            <div className="section-divider mx-auto mt-4" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div className="space-y-5 text-left order-2 lg:order-1">
              <p className="text-base md:text-lg leading-relaxed" style={{ color: "#4B5563" }}>I am 5 years old and I've had Rett syndrome since birth.</p>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: "#4B5563" }}>I can't walk. I can't use my hands. I can't even say what I'm feeling right now.</p>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: "#4B5563" }}>But I'm still here – with a huge heart and a lot of love for this world.</p>
              <div className="py-4 px-6 rounded-xl" style={{ background: "rgba(149, 225, 187, 0.3)" }}>
                <p className="font-accent text-xl md:text-2xl leading-relaxed" style={{ color: "#b46eb6" }}>I'm just missing one piece of the puzzle. And research knows exactly where it is.</p>
              </div>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: "#4B5563" }}>Maybe I'm sick today – but who says it has to stay this way forever?</p>
              <p className="text-base md:text-lg leading-relaxed font-semibold" style={{ color: "#1F2937" }}>There's always a solution. So why shouldn't there be hope for me?</p>
              <div className="pt-4 space-y-2">
                <p className="text-lg md:text-xl font-bold" style={{ color: "#b46eb6" }}>But for that, I need you.</p>
                <p className="text-base md:text-lg" style={{ color: "#4B5563" }}>You can help find my missing puzzle piece. You can be part of my story.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 md:gap-4 order-1 lg:order-2">
              {ELLA_PHOTOS.map((photo, idx) => (
                <button key={idx} onClick={() => setLightbox(photo)} className="overflow-hidden rounded-2xl shadow-md aspect-square gallery-photo">
                  <img src={photo.url} alt={photo.alt} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Rett Syndrome */}
      <section id="rett-en" className="py-20 md:py-28" style={{ background: "#F8F9FA" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <p className="font-accent text-xl md:text-2xl mb-2" style={{ color: "#7DD4B5" }}>Understanding the condition</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight" style={{ color: "#1F2937" }}>What is Rett Syndrome?</h2>
            <div className="section-divider mx-auto mt-4" />
          </div>
          <div className="max-w-3xl mx-auto mb-12 text-center">
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "#4B5563" }}>
              Rett syndrome is a rare genetic neurological disorder caused by a mutation in the <strong>MECP2 gene</strong>. It almost exclusively affects girls and leads to severe impairments in nearly all areas of life. There is currently no cure – but research is making great progress.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {symptoms.map((s, i) => (
              <Card key={i} className="border-0 shadow-md hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-bold mb-2" style={{ color: "#b46eb6" }}>{s.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "#4B5563" }}>{s.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Event Stats */}
      <section id="event-en" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <p className="font-accent text-xl md:text-2xl mb-2" style={{ color: "#7DD4B5" }}>The Event</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight" style={{ color: "#1F2937" }}>Run for Rett – Running Together, Helping Together</h2>
            <div className="section-divider mx-auto mt-4" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <Card key={idx} className="border-0 shadow-md bg-white text-center">
                  <CardContent className="p-6 md:p-8">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: "rgba(149, 225, 187, 0.5)" }}>
                      <Icon className="w-6 h-6" style={{ color: stat.color }} />
                    </div>
                    <p className="font-heading text-2xl md:text-3xl font-extrabold" style={{ color: stat.color }}>{stat.value}</p>
                    <p className="text-sm mt-1" style={{ color: "#9CA3AF" }}>{stat.label}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "#4B5563" }}>
              In <strong>2025</strong> and <strong>2026</strong>, two charity runs took place in Herrnried, Germany. Thanks to <strong>1,814 runners</strong>, <strong>130 volunteers</strong>, over <strong>2,000 visitors</strong> and many generous donors, we raised over <strong style={{ color: "#b46eb6" }}>€140,000</strong>.
            </p>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "#4B5563" }}>
              These donations were passed on to the <strong style={{ color: "#b46eb6" }}>Rett Syndrome Research Trust (RSRT)</strong>, supporting research projects worldwide.
            </p>
            <div className="pt-4 px-6 py-4 rounded-xl inline-block" style={{ background: "rgba(180, 110, 182, 0.08)" }}>
              <p className="text-base font-semibold" style={{ color: "#b46eb6" }}>We're taking a break in 2027 – but we'll be back in 2028!</p>
            </div>
          </div>
        </div>
      </section>

      {/* Donate */}
      <section id="donate-en" className="py-20 md:py-28 relative overflow-hidden" style={{ background: "#b46eb6" }}>
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full opacity-10" style={{ background: "#7DD4B5", filter: "blur(80px)" }} />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">Support Rett Syndrome Research</h2>
          <p className="text-base md:text-lg mb-8" style={{ color: "rgba(255,255,255,0.85)" }}>Every donation brings us closer to a cure. 100% of all proceeds go directly to research.</p>
          <Card className="border-0 shadow-2xl max-w-md mx-auto">
            <CardContent className="p-6 md:p-8 space-y-4">
              <p className="text-sm font-semibold" style={{ color: "#1F2937" }}>Donation Information</p>
              <div className="text-left space-y-2">
                <p className="text-sm" style={{ color: "#4B5563" }}>Recipient: <strong>Zukunft durch medizinische Forschung e.V.</strong></p>
                <p className="text-sm" style={{ color: "#4B5563" }}>IBAN: <strong>DE26 7506 9015 0001 1388 60</strong></p>
                <p className="text-sm" style={{ color: "#4B5563" }}>BIC: <strong>GENODEF1NEA</strong></p>
                <p className="text-sm" style={{ color: "#4B5563" }}>Reference: <strong>Run for Rett</strong></p>
              </div>
              <a href="https://www.gofundme.com/run-for-rett" target="_blank" rel="noopener noreferrer" className="block">
                <Button className="w-full rounded-full py-5 text-base font-bold bg-[#b46eb6] hover:bg-[#8f5691] text-white">
                  <Heart className="w-4 h-4 mr-2" /> Donate via GoFundMe
                </Button>
              </a>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10" style={{ background: "#1F2937" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/logo-runforrett.svg" alt="Run for Rett Logo" className="h-8 w-auto brightness-0 invert" />
              <p className="text-sm" style={{ color: "rgba(255,255,255,0.5)" }}>Running for a future without Rett syndrome</p>
            </div>
            <div className="flex items-center gap-4">
              <a href="https://www.instagram.com/run.for.rett/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full flex items-center justify-center hover:scale-110 transition-transform" style={{ background: "rgba(149,225,187,0.15)" }}>
                <Instagram className="w-4 h-4" style={{ color: "#7DD4B5" }} />
              </a>
            </div>
            <div className="flex items-center gap-4 text-sm" style={{ color: "rgba(255,255,255,0.4)" }}>
              <Link to="/impressum" className="hover:text-white transition-colors">Legal Notice</Link>
              <Link to="/datenschutz" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/" className="hover:text-white transition-colors">Deutsch</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-3xl p-2 bg-white rounded-2xl">
          {lightbox && <img src={lightbox.url} alt={lightbox.alt} className="w-full h-auto rounded-xl" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
